import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SubOrganisationService } from "../../services/subOrganisationService";
import logger from "../../core/utils/logger";

// Async thunk for getting all sub-organisations
export const getAllSubOrganisations = createAsyncThunk(
    'subOrganisation/getAllSubOrganisations',
    async (_, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.getAllSubOrganisations();
            logger.log("All sub-organisations response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error getting all sub-organisations');
        }
    }
);

// Async thunk for getting sub-organisation by ID
export const getSubOrganisationById = createAsyncThunk(
    'subOrganisation/getSubOrganisationById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.getSubOrganisationById(id);
            logger.log("Sub-organisation by ID response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error getting sub-organisation by ID');
        }
    }
);

// Async thunk for getting current user's sub-organisations
export const getCurrentSubOrganisations = createAsyncThunk(
    'subOrganisation/getCurrentSubOrganisations',
    async (_, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.getCurrentSubOrganisations();
            logger.log("Current sub-organisations response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error getting current sub-organisations');
        }
    }
);

// Async thunk for creating a new sub-organisation
export const createSubOrganisation = createAsyncThunk(
    'subOrganisation/createSubOrganisation',
    async (subOrganisationData, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.createSubOrganisation(subOrganisationData);
            logger.log("Sub-organisation creation response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error creating sub-organisation');
        }
    }
);

// Async thunk for updating current sub-organisation
export const updateCurrentSubOrganisation = createAsyncThunk(
    'subOrganisation/updateCurrentSubOrganisation',
    async (subOrganisationData, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.updateCurrentSubOrganisation(subOrganisationData);
            logger.log("Current sub-organisation update response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error updating current sub-organisation');
        }
    }
);

// Async thunk for updating sub-organisation by ID
export const updateSubOrganisation = createAsyncThunk(
    'subOrganisation/updateSubOrganisation',
    async ({ id, subOrganisationData }, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.updateSubOrganisation(id, subOrganisationData);
            logger.log("Sub-organisation update response:", response);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error updating sub-organisation');
        }
    }
);

// Async thunk for patching sub-organisation
export const patchSubOrganisation = createAsyncThunk(
    'subOrganisation/patchSubOrganisation',
    async ({ id, patchData }, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.patchSubOrganisation(id, patchData);
            logger.log("Sub-organisation patch response:", response);
            return { id, patchData, success: response };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error patching sub-organisation');
        }
    }
);

// Async thunk for deleting sub-organisation by ID
export const deleteSubOrganisation = createAsyncThunk(
    'subOrganisation/deleteSubOrganisation',
    async (id, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.deleteSubOrganisation(id);
            logger.log("Sub-organisation deletion response:", response);
            return { id, success: response };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error deleting sub-organisation');
        }
    }
);

// Async thunk for deleting sub-organisation by organization ID
export const deleteSubOrganisationByOrganizationId = createAsyncThunk(
    'subOrganisation/deleteSubOrganisationByOrganizationId',
    async (organizationId, { rejectWithValue }) => {
        try {
            const response = await SubOrganisationService.deleteSubOrganisationByOrganizationId(organizationId);
            logger.log("Sub-organisation deletion by organization ID response:", response);
            return { organizationId, success: response };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Error deleting sub-organisation by organization ID');
        }
    }
);

const initialState = {
    subOrganisations: {
        loading: false,
        error: null,
        data: []
    },
    currentSubOrganisation: {
        loading: false,
        error: null,
        data: null
    },
    operations: {
        creating: false,
        updating: false,
        deleting: false,
        error: null
    }
};

const subOrganisationSlice = createSlice({
    name: 'subOrganisationData',
    initialState,
    reducers: {
        clearState: (state) => {
            state.subOrganisations.loading = false;
            state.subOrganisations.error = null;
            state.subOrganisations.data = [];
            state.currentSubOrganisation.loading = false;
            state.currentSubOrganisation.error = null;
            state.currentSubOrganisation.data = null;
            state.operations.creating = false;
            state.operations.updating = false;
            state.operations.deleting = false;
            state.operations.error = null;
        },
        clearErrors: (state) => {
            state.subOrganisations.error = null;
            state.currentSubOrganisation.error = null;
            state.operations.error = null;
        },
        setCurrentSubOrganisation: (state, action) => {
            state.currentSubOrganisation.data = action.payload;
        }
    },
    extraReducers: (builder) => {
        // Get all sub-organisations
        builder.addCase(getAllSubOrganisations.pending, (state) => {
            state.subOrganisations.loading = true;
            state.subOrganisations.error = null;
        });
        builder.addCase(getAllSubOrganisations.fulfilled, (state, action) => {
            state.subOrganisations.loading = false;
            state.subOrganisations.error = null;
            state.subOrganisations.data = action.payload;
        });
        builder.addCase(getAllSubOrganisations.rejected, (state, action) => {
            state.subOrganisations.loading = false;
            state.subOrganisations.error = action.payload;
            state.subOrganisations.data = [];
        });

        // Get sub-organisation by ID
        builder.addCase(getSubOrganisationById.pending, (state) => {
            state.currentSubOrganisation.loading = true;
            state.currentSubOrganisation.error = null;
        });
        builder.addCase(getSubOrganisationById.fulfilled, (state, action) => {
            state.currentSubOrganisation.loading = false;
            state.currentSubOrganisation.error = null;
            state.currentSubOrganisation.data = action.payload;
        });
        builder.addCase(getSubOrganisationById.rejected, (state, action) => {
            state.currentSubOrganisation.loading = false;
            state.currentSubOrganisation.error = action.payload;
            state.currentSubOrganisation.data = null;
        });

        // Get current sub-organisations
        builder.addCase(getCurrentSubOrganisations.pending, (state) => {
            state.subOrganisations.loading = true;
            state.subOrganisations.error = null;
        });
        builder.addCase(getCurrentSubOrganisations.fulfilled, (state, action) => {
            state.subOrganisations.loading = false;
            state.subOrganisations.error = null;
            state.subOrganisations.data = action.payload;
        });
        builder.addCase(getCurrentSubOrganisations.rejected, (state, action) => {
            state.subOrganisations.loading = false;
            state.subOrganisations.error = action.payload;
            state.subOrganisations.data = [];
        });

        // Create sub-organisation
        builder.addCase(createSubOrganisation.pending, (state) => {
            state.operations.creating = true;
            state.operations.error = null;
        });
        builder.addCase(createSubOrganisation.fulfilled, (state, action) => {
            state.operations.creating = false;
            state.operations.error = null;
            state.subOrganisations.data.push(action.payload);
        });
        builder.addCase(createSubOrganisation.rejected, (state, action) => {
            state.operations.creating = false;
            state.operations.error = action.payload;
        });

        // Update current sub-organisation
        builder.addCase(updateCurrentSubOrganisation.pending, (state) => {
            state.operations.updating = true;
            state.operations.error = null;
        });
        builder.addCase(updateCurrentSubOrganisation.fulfilled, (state, action) => {
            state.operations.updating = false;
            state.operations.error = null;
            state.currentSubOrganisation.data = action.payload;
            // Update in the list if it exists
            const index = state.subOrganisations.data.findIndex(sub => sub.id === action.payload.id);
            if (index !== -1) {
                state.subOrganisations.data[index] = action.payload;
            }
        });
        builder.addCase(updateCurrentSubOrganisation.rejected, (state, action) => {
            state.operations.updating = false;
            state.operations.error = action.payload;
        });

        // Update sub-organisation by ID
        builder.addCase(updateSubOrganisation.pending, (state) => {
            state.operations.updating = true;
            state.operations.error = null;
        });
        builder.addCase(updateSubOrganisation.fulfilled, (state, action) => {
            state.operations.updating = false;
            state.operations.error = null;
            const updatedSubOrg = action.payload;
            const index = state.subOrganisations.data.findIndex(sub => sub.id === updatedSubOrg.id);
            if (index !== -1) {
                state.subOrganisations.data[index] = updatedSubOrg;
            }
            // Update current if it's the same
            if (state.currentSubOrganisation.data?.id === updatedSubOrg.id) {
                state.currentSubOrganisation.data = updatedSubOrg;
            }
        });
        builder.addCase(updateSubOrganisation.rejected, (state, action) => {
            state.operations.updating = false;
            state.operations.error = action.payload;
        });

        // Patch sub-organisation
        builder.addCase(patchSubOrganisation.pending, (state) => {
            state.operations.updating = true;
            state.operations.error = null;
        });
        builder.addCase(patchSubOrganisation.fulfilled, (state, action) => {
            state.operations.updating = false;
            state.operations.error = null;
            // Note: For patch, we might need to refetch the data or apply the patch locally
            // This depends on what the backend returns
        });
        builder.addCase(patchSubOrganisation.rejected, (state, action) => {
            state.operations.updating = false;
            state.operations.error = action.payload;
        });

        // Delete sub-organisation
        builder.addCase(deleteSubOrganisation.pending, (state) => {
            state.operations.deleting = true;
            state.operations.error = null;
        });
        builder.addCase(deleteSubOrganisation.fulfilled, (state, action) => {
            state.operations.deleting = false;
            state.operations.error = null;
            const { id } = action.payload;
            state.subOrganisations.data = state.subOrganisations.data.filter(sub => sub.id !== id);
            // Clear current if it was deleted
            if (state.currentSubOrganisation.data?.id === id) {
                state.currentSubOrganisation.data = null;
            }
        });
        builder.addCase(deleteSubOrganisation.rejected, (state, action) => {
            state.operations.deleting = false;
            state.operations.error = action.payload;
        });

        // Delete sub-organisation by organization ID
        builder.addCase(deleteSubOrganisationByOrganizationId.pending, (state) => {
            state.operations.deleting = true;
            state.operations.error = null;
        });
        builder.addCase(deleteSubOrganisationByOrganizationId.fulfilled, (state, action) => {
            state.operations.deleting = false;
            state.operations.error = null;
            const { organizationId } = action.payload;
            state.subOrganisations.data = state.subOrganisations.data.filter(sub => sub.organizationId !== organizationId);
            // Clear current if it was deleted
            if (state.currentSubOrganisation.data?.organizationId === organizationId) {
                state.currentSubOrganisation.data = null;
            }
        });
        builder.addCase(deleteSubOrganisationByOrganizationId.rejected, (state, action) => {
            state.operations.deleting = false;
            state.operations.error = action.payload;
        });
    },
});

export const { clearState, clearErrors, setCurrentSubOrganisation } = subOrganisationSlice.actions;
export default subOrganisationSlice.reducer;
